import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "@/lib/scroll";
import { trackAuditCta } from "@/tracking/analytics";
import { useBooking } from "./BookingProvider";

const LINKS = [
    { label: "How It Works", id: "how-it-works" },
    { label: "Services", id: "services" },
    { label: "Why Scalehaus", id: "why" },
    { label: "FAQ", id: "faq" },
];

const goTo = (id) => scrollToId(id);

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const openBooking = useBooking();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleCta = (e) => {
        e.preventDefault();
        setOpen(false);
        trackAuditCta("navbar");
        openBooking();
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 border-b ${
                scrolled || open
                    ? "bg-ink-900/80 backdrop-blur-xl border-white/10"
                    : "bg-ink-900/40 backdrop-blur-md border-white/5"
            }`}
        >
            <nav
                className="shell flex items-center justify-between h-16 md:h-20"
                aria-label="Primary"
            >
                <Logo
                    onClick={(e) => {
                        e.preventDefault();
                        setOpen(false);
                        goTo("top");
                    }}
                />
                <div className="hidden md:flex items-center gap-9">
                    {LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            data-testid={`nav-link-${l.id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                goTo(l.id);
                            }}
                            className="text-sm text-zinc-400 transition-colors duration-300 hover:text-white"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="#audit"
                        data-testid="nav-cta-button"
                        onClick={handleCta}
                        className="bg-brass-500 text-ink-900 text-sm font-semibold px-5 py-2.5 transition-all duration-300 hover:bg-brass-400 active:scale-[0.98]"
                    >
                        Explore My Options
                    </a>
                </div>
                <button
                    className="md:hidden text-white p-2 -mr-2"
                    aria-expanded={open}
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </nav>
            {open && (
                <div className="md:hidden bg-ink-900/95 backdrop-blur-xl border-t border-white/10">
                    <div className="shell py-6 flex flex-col gap-5">
                        {LINKS.map((l) => (
                            <a
                                key={l.id}
                                href={`#${l.id}`}
                                data-testid={`mobile-nav-link-${l.id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpen(false);
                                    goTo(l.id);
                                }}
                                className="text-base text-zinc-300 hover:text-white"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#audit"
                            data-testid="mobile-nav-cta-button"
                            onClick={handleCta}
                            className="bg-brass-500 text-ink-900 text-sm font-semibold px-5 py-3.5 text-center"
                        >
                            Explore My Options
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
